import { useMutation } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Shared impersonation-start binding for `POST /api/v1/me/impersonation/start`.
 *
 * Both impersonation launchers post to the same `me` endpoint: the SiteAdmin
 * cross-tenant launcher (slice 17) and the customer-portal Administrator
 * "View as user" launcher (slice 22). The hook lives here — next to `whoami` —
 * rather than inside either feature so neither depends on the other. The target
 * scope (own-customer vs. any-customer) is enforced server-side; the request
 * body is identical.
 */

/** Body for `POST /api/v1/me/impersonation/start`. */
export interface StartImpersonationRequest {
  targetUserId: string;
  reason?: string | null;
}

/** `StartImpersonationResponse` — the opened session + target. */
export interface StartImpersonationResponse {
  sessionId: string;
  target: {
    userId: string;
    customerId: string | null;
    email: string;
    fullName: string;
  };
  startedAt: string;
  reason: string;
}

/**
 * Start impersonating a user — `POST /api/v1/me/impersonation/start` (real
 * endpoint). A 409 (`impersonation.session_already_active`) means a session is
 * already open; a 403 (`OutsideAdministratorScope`) means the target is outside
 * the caller's own customer. The caller invalidates `['whoami']` on success so
 * the shell rebuilds as the target (the foundation `ImpersonationBanner` ends
 * it).
 */
export function useStartImpersonation() {
  return useMutation<StartImpersonationResponse, unknown, StartImpersonationRequest>({
    mutationFn: async (body) => {
      const { data } = await apiClient.post<StartImpersonationResponse>(
        '/me/impersonation/start',
        body,
      );
      return data;
    },
  });
}
